// src/features/inventory/pages/BatchDetailPage/index.jsx
import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Inventory2Outlined as StockIcon,
  Timer as ExpiryIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useLots } from '../../hooks/useLots';
import { useInventoryLotPermissions, useInventoryStockPermissions } from '../../../auth/hooks/usePermissions';
import { getDateLocale } from '../../../../i18n/LocalizedDateDistance';
import { formatDistanceToNow } from 'date-fns';
import BatchHeader from './BatchHeader';
import InfoTip from '../../../../components/common/InfoTip';
import BatchTabs from './tabs';
import { useNavigationStack } from '../../../../context/NavigationStackContext';

const LOT_ALERT_SEVERITY = {
  expired:     'error',
  recalled:    'warning',
  quarantined: 'warning',
};

function StatCard({ icon: Icon, label, value, valueColor, caption, captionColor, info }) {
  return (
    <Card
      elevation={0}
      sx={{
        flex: 1,
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <InfoTip title={info} />
      </Box>
      {value}
      {caption && (
        <Typography variant="caption" color={captionColor || 'text.secondary'} sx={{ mt: 0.25 }}>
          {caption}
        </Typography>
      )}
    </Card>
  );
}

function StockCard({ lot, t }) {
  const pct = lot.initial_quantity > 0
    ? Math.round((lot.quantity_on_hand / lot.initial_quantity) * 100)
    : 0;
  const locationBalances = (lot.location_balances ?? []).filter(b => b.quantity > 0);

  return (
    <StatCard
      icon={StockIcon}
      label={t('inventory:batches.detail.qty_on_hand')}
      info={t('inventory:help.batches.qty_on_hand')}
      value={
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
            <Typography variant="h6" fontWeight={700}>
              {lot.quantity_on_hand.toLocaleString()} {t('inventory:batches.detail.remaining_suffix')}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {pct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(pct, 100)}
            sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover' }}
          />
          {locationBalances.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: `repeat(${locationBalances.length}, auto) 1fr`, columnGap: 2.5 }}>
              {locationBalances.map((b) => (
                <Typography key={`name-${b.location_id}`} variant="caption" color="text.disabled" fontWeight={500} sx={{ letterSpacing: '0.04em' }}>
                  {b.location_id == null ? t('inventory:items.no_location') : b.location_name}
                </Typography>
              ))}
              <span />
              {locationBalances.map((b) => (
                <Typography key={`qty-${b.location_id}`} variant="body2">
                  <b>{b.quantity}</b>
                </Typography>
              ))}
              <span />
            </Box>
          )}
        </Box>
      }
      caption={`${t('inventory:batches.detail.of_initial')} ${lot.initial_quantity.toLocaleString()}`}
    />
  );
}

function ProductionDateCard({ lot, t }) {
  const { i18n } = useTranslation();
  if (!lot.production_date) return null;

  const date = new Date(lot.production_date).toLocaleDateString();
  const locale = getDateLocale(i18n.language);
  const distanceText = formatDistanceToNow(new Date(lot.production_date), { addSuffix: true, locale });

  return (
    <StatCard
      icon={CalendarIcon}
      label={t('inventory:batches.detail.production_date')}
      info={t('inventory:help.batches.production_date')}
      value={
        <Typography variant="h6" fontWeight={700}>{date}</Typography>
      }
      caption={distanceText}
    />
  );
}

function ExpiryDateCard({ lot, t }) {
  const { i18n } = useTranslation();
  if (!lot.expiry_date) return null;

  const isExpired = lot.status?.toLowerCase() === 'expired';
  const date = new Date(lot.expiry_date).toLocaleDateString();
  const locale = getDateLocale(i18n.language);
  const distanceText = formatDistanceToNow(new Date(lot.expiry_date), { addSuffix: true, locale });

  return (
    <StatCard
      icon={ExpiryIcon}
      label={t('inventory:batches.detail.expiry_date')}
      info={t('inventory:help.batches.expiry_date')}
      value={
        <Typography variant="h6" fontWeight={700} color={isExpired ? 'error.main' : 'text.primary'}>
          {date}
        </Typography>
      }
      caption={distanceText}
      captionColor={isExpired ? 'error.main' : 'text.secondary'}
    />
  );
}

export default function BatchDetailPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const { lotId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { canUpdate } = useInventoryLotPermissions();
  const { canRecord } = useInventoryStockPermissions();

  const {
    currentLot,
    loading,
    error,
    clearError,
    fetchLotById,
  } = useLots();

  useEffect(() => {
    fetchLotById(lotId);
  }, [fetchLotById, lotId]);

  const handleStatusUpdated = useCallback(() => {
    fetchLotById(lotId);
  }, [fetchLotById, lotId]);

  if (loading && !currentLot) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!currentLot) return null;

  const status = currentLot.status?.toLowerCase();
  const alertSeverity = LOT_ALERT_SEVERITY[status];

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <BatchHeader
        lot={currentLot}
        canUpdate={canUpdate}
        canRecord={canRecord}
        onBack={() => navigateBack('..')}
        onStatusUpdated={handleStatusUpdated}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {alertSeverity && (
        <Alert severity={alertSeverity} sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {t(`inventory:batches.detail.alert_${status}_title`)}
          </Typography>
          <Typography variant="body2">
            {t(`inventory:batches.detail.alert_${status}_body`, {
              date: currentLot.expiry_date ? new Date(currentLot.expiry_date).toLocaleDateString() : '—',
              qty: currentLot.quantity_on_hand.toLocaleString(),
            })}
          </Typography>
        </Alert>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' },
        gap: 2,
        mb: 3,
      }}>
        <StockCard lot={currentLot} t={t} />
        <ProductionDateCard lot={currentLot} t={t} />
        <ExpiryDateCard lot={currentLot} t={t} />
      </Box>

      <BatchTabs
        lotId={lotId}
        lot={currentLot}
        canRecord={canRecord}
        onLotUpdated={() => fetchLotById(lotId)}
      />
    </Box>
  );
}
